import React, { useState } from 'react';
import {
  Box,
  Button,
  Input,
  Stack,
  Heading,
  Flex,
  Text,
  Icon,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { useToast } from '@chakra-ui/toast';
import { FormControl, FormLabel } from '@chakra-ui/form-control';
import { Navigate } from 'react-router-dom';
import { FiUser, FiLock } from 'react-icons/fi';

const LoginForm = ({ onLogin, apiBase }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loggedIn, setLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/v1/auth/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('telemetry_token', data.token);
        onLogin();
        setLoggedIn(true);
      } else {
        toast({
          title: 'Login failed',
          description: 'Invalid credentials',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch {
      toast({
        title: 'Login failed',
        description: 'Connection error. Please check the server.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loggedIn) {
    return <Navigate to="/" />;
  }

  return (
    <Flex
      minHeight="100vh"
      align="center"
      justify="center"
      bgGradient="linear(to-br, purple.600, purple.900)"
    >
      <Box
        bg="white"
        px={10}
        py={12}
        borderRadius="2xl"
        boxShadow="2xl"
        width="100%"
        maxWidth="420px"
        mx={4}
      >
        <Stack as="form" spacing={6} onSubmit={handleLogin}>
          {/* Logo */}
          <Flex direction="column" align="center" mb={4}>
            <Box
              w={20}
              h={20}
              borderRadius="full"
              overflow="hidden"
              bg="purple.500"
              p={1}
              mb={4}
              boxShadow="lg"
            >
              <img src="/static/selwyn-logo.png" alt="Selwyn" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            </Box>
            <Heading size="lg" color="gray.800">Telemetry</Heading>
            <Text color="gray.500" fontSize="sm" mt={1}>Sign in to your account</Text>
          </Flex>

          <FormControl isRequired>
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">Username</FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiUser} color="gray.400" />
              </InputLeftElement>
              <Input
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                size="lg"
                borderRadius="lg"
                focusBorderColor="purple.500"
              />
            </InputGroup>
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">Password</FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiLock} color="gray.400" />
              </InputLeftElement>
              <Input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                size="lg"
                borderRadius="lg"
                focusBorderColor="purple.500"
              />
            </InputGroup>
          </FormControl>

          <Button
            type="submit"
            size="lg"
            bgGradient="linear(to-r, purple.500, purple.600)"
            color="white"
            _hover={{
              bgGradient: 'linear(to-r, purple.600, purple.700)',
              transform: 'translateY(-2px)',
              boxShadow: 'lg',
            }}
            _active={{
              transform: 'translateY(0)',
            }}
            borderRadius="lg"
            isLoading={isLoading}
            loadingText="Signing in..."
            transition="all 0.2s"
          >
            Sign In
          </Button>
        </Stack>
      </Box>
    </Flex>
  );
};

export default LoginForm;
